import React, { useState } from 'react';
import { PageLayout } from '../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { SettingsIcon, ShieldIcon, BellIcon, DatabaseIcon, PaletteIcon, InfoIcon, CheckIcon, ChevronRightIcon } from 'lucide-react';
interface SettingToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function SettingToggle({
  label,
  description,
  value,
  onChange
}: SettingToggleProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-565">
      <div className="flex-1 pr-4" data-id="element-566">
        <div className="text-sm font-medium text-gray-900" data-id="element-567">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-568">{description}</div>
      </div>
      <button onClick={() => onChange(!value)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${value ? 'bg-blue-600' : 'bg-gray-200'}`} role="switch" aria-checked={value} data-id="element-569">
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4.5' : 'translate-x-0.5'}`} style={{
        transform: value ? 'translateX(18px)' : 'translateX(2px)'
      }} data-id="element-570" />
      </button>
    </div>;
}
interface SettingSelectProps {
  label: string;
  description: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  onChange: (v: string) => void;
}
function SettingSelect({
  label,
  description,
  value,
  options,
  onChange
}: SettingSelectProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-571">
      <div className="flex-1 pr-4" data-id="element-572">
        <div className="text-sm font-medium text-gray-900" data-id="element-573">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-574">{description}</div>
      </div>
      <select value={value} onChange={e => onChange(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-575">
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-576">
            {opt.label}
          </option>)}
      </select>
    </div>;
}
const sections = [{
  id: 'general',
  label: 'Algemeen',
  icon: <SettingsIcon className="w-4 h-4" data-id="element-577" />
}, {
  id: 'governance',
  label: 'Governance',
  icon: <ShieldIcon className="w-4 h-4" data-id="element-578" />
}, {
  id: 'notifications',
  label: 'Meldingen',
  icon: <BellIcon className="w-4 h-4" data-id="element-579" />
}, {
  id: 'data',
  label: 'Data & Opslag',
  icon: <DatabaseIcon className="w-4 h-4" data-id="element-580" />
}] as const;
type SectionId = (typeof sections)[number]['id'];
export function AdminSettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [saved, setSaved] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    // General
    strictMode: false,
    debugLogging: false,
    showConnectionBadges: true,
    language: 'nl',
    // Governance
    autoValidate: true,
    blockOnError: false,
    warnOnWarning: true,
    governanceLevel: 'standard',
    // Notifications
    notifyOnError: true,
    notifyOnWarning: false,
    notifyOnNewAccount: true,
    // Data
    persistSessions: true,
    maxSessionHistory: '1000',
    autoCleanup: false
  });
  const update = (key: keyof typeof settings, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <PageLayout title="Instellingen" description="Systeem- en governance-instellingen" actions={<div className="flex items-center gap-2" data-id="element-582">
          <ConnectionBadge status="live" data-id="element-583" />
          <button onClick={handleSave} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-584">
            {saved ? <>
                <CheckIcon className="w-4 h-4" data-id="element-585" />
                Opgeslagen
              </> : 'Opslaan'}
          </button>
        </div>} data-id="element-581">
      <div className="flex flex-col md:flex-row gap-6" data-id="element-586">
        {/* Sidebar nav */}
        <div className="w-full md:w-48 flex-shrink-0" data-id="element-587">
          <nav className="flex md:flex-col gap-2 overflow-x-auto pb-2 md:pb-0 md:space-y-1" data-id="element-588">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex-shrink-0 flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === section.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-589">
                {section.icon}
                {section.label}
              </button>)}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-590">
          {activeSection === 'general' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-591">
              <div className="px-5 py-4 border-b border-gray-100" data-id="element-592">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-593">
                  Algemene instellingen
                </h2>
              </div>
              <div className="px-5" data-id="element-594">
                <SettingToggle label="Strict mode" description="Striktere validatie en foutafhandeling" value={settings.strictMode} onChange={v => update('strictMode', v)} data-id="element-595" />
                <SettingToggle label="Debug logging" description="Uitgebreide logging in de browser console" value={settings.debugLogging} onChange={v => update('debugLogging', v)} data-id="element-596" />
                <SettingToggle label="Verbindingsbadges tonen" description="Toon live/mock badges op elke pagina" value={settings.showConnectionBadges} onChange={v => update('showConnectionBadges', v)} data-id="element-597" />
                <SettingSelect label="Taal" description="Taal van de admin interface" value={settings.language} options={[{
              value: 'nl',
              label: 'Nederlands'
            }, {
              value: 'en',
              label: 'English'
            }]} onChange={v => update('language', v)} data-id="element-598" />
              </div>
            </div>}

          {activeSection === 'governance' && <div className="space-y-4" data-id="element-599">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-600">
                <div className="px-5 py-4 border-b border-gray-100" data-id="element-601">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-602">
                    Governance pipeline
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-603">
                    Instellingen voor de validatie- en governance-pipeline
                  </p>
                </div>
                <div className="px-5" data-id="element-604">
                  <SettingToggle label="Automatisch valideren" description="Valideer vragen automatisch bij het laden" value={settings.autoValidate} onChange={v => update('autoValidate', v)} data-id="element-605" />
                  <SettingToggle label="Blokkeren bij fouten" description="Blokkeer het systeem bij governance-fouten" value={settings.blockOnError} onChange={v => update('blockOnError', v)} data-id="element-606" />
                  <SettingToggle label="Waarschuwen bij warnings" description="Toon waarschuwingen bij governance-issues" value={settings.warnOnWarning} onChange={v => update('warnOnWarning', v)} data-id="element-607" />
                  <SettingSelect label="Governance niveau" description="Strengheid van de governance-regels" value={settings.governanceLevel} options={[{
                value: 'lenient',
                label: 'Licht (alleen fouten)'
              }, {
                value: 'standard',
                label: 'Standaard (fouten + warnings)'
              }, {
                value: 'strict',
                label: 'Strikt (alles)'
              }]} onChange={v => update('governanceLevel', v)} data-id="element-608" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 flex items-start gap-3" data-id="element-609">
                <InfoIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-610" />
                <div data-id="element-611">
                  <p className="text-sm font-medium text-blue-800" data-id="element-612">
                    Governance pipeline
                  </p>
                  <p className="text-xs text-blue-600 mt-1" data-id="element-613">
                    De governance pipeline controleert alle vragen op
                    schema-validatie, type-regels, inhoudskwaliteit en visuele
                    governance. Zie de <strong data-id="element-614">Validatieregels</strong> pagina
                    voor een volledige compliance-check.
                  </p>
                </div>
              </div>
            </div>}

          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-615">
              <div className="px-5 py-4 border-b border-gray-100" data-id="element-616">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-617">
                  Meldingsinstellingen
                </h2>
              </div>
              <div className="px-5" data-id="element-618">
                <SettingToggle label="Melden bij fouten" description="Stuur een melding bij governance-fouten" value={settings.notifyOnError} onChange={v => update('notifyOnError', v)} data-id="element-619" />
                <SettingToggle label="Melden bij waarschuwingen" description="Stuur een melding bij governance-waarschuwingen" value={settings.notifyOnWarning} onChange={v => update('notifyOnWarning', v)} data-id="element-620" />
                <SettingToggle label="Melden bij nieuw account" description="Stuur een melding bij een nieuw account" value={settings.notifyOnNewAccount} onChange={v => update('notifyOnNewAccount', v)} data-id="element-621" />
              </div>
            </div>}

          {activeSection === 'data' && <div className="space-y-4" data-id="element-622">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-623">
                <div className="px-5 py-4 border-b border-gray-100" data-id="element-624">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-625">
                    Data & opslag
                  </h2>
                </div>
                <div className="px-5" data-id="element-626">
                  <SettingToggle label="Sessies bewaren" description="Bewaar oefensessies in lokale opslag" value={settings.persistSessions} onChange={v => update('persistSessions', v)} data-id="element-627" />
                  <SettingToggle label="Automatisch opruimen" description="Verwijder oude sessies automatisch" value={settings.autoCleanup} onChange={v => update('autoCleanup', v)} data-id="element-628" />
                  <SettingSelect label="Max. sessiegeschiedenis" description="Maximum aantal sessies om te bewaren" value={settings.maxSessionHistory} options={[{
                value: '100',
                label: '100 sessies'
              }, {
                value: '500',
                label: '500 sessies'
              }, {
                value: '1000',
                label: '1.000 sessies'
              }, {
                value: '5000',
                label: '5.000 sessies'
              }]} onChange={v => update('maxSessionHistory', v)} data-id="element-629" />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3" data-id="element-630">
                <InfoIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-631" />
                <div data-id="element-632">
                  <p className="text-sm font-medium text-amber-800" data-id="element-633">
                    Lokale opslag
                  </p>
                  <p className="text-xs text-amber-600 mt-1" data-id="element-634">
                    Alle data wordt lokaal opgeslagen in de browser. Er is geen
                    externe database verbinding. Data gaat verloren bij het
                    wissen van de browser-cache.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </PageLayout>;
}